import React, { FC, ReactNode, useCallback, useMemo, useState } from 'react';
import classNames from 'classnames';
import { TechChip } from '../../../components/videoSection/VideoCard';

export interface MosaicTile {
  key: string | number;
  src?: string;
  fallback?: ReactNode;
}

interface Props {
  tiles: MosaicTile[];
  total: number;
  countLabel: string;
  emptyTitle: string;
  emptyAction?: { label: string; onClick: () => void };
}

const HATCH = 'repeating-linear-gradient(45deg, var(--surface) 0 7px, var(--surface-2) 7px 14px)';

const Frame: FC<{ tile?: MosaicTile; className?: string; onFail?: () => void }> = ({
  tile,
  className,
  onFail,
}) => (
  <span
    className={classNames('relative block overflow-hidden bg-track', className)}
    style={tile?.src ? undefined : { background: HATCH }}
  >
    {tile?.src ? (
      <img
        src={tile.src}
        alt=""
        loading="lazy"
        className="w-full h-full object-cover"
        onError={onFail}
      />
    ) : (
      <span className="absolute inset-0 flex items-center justify-center text-muted">{tile?.fallback}</span>
    )}
  </span>
);

export const ShortlistMosaic: FC<Props> = ({
  tiles,
  total,
  countLabel,
  emptyTitle,
  emptyAction,
}) => {
  const [failed, setFailed] = useState<Record<string, true>>({});
  const markFailed = useCallback(
    (key: string | number) => setFailed((prev) => ({ ...prev, [String(key)]: true })),
    []
  );

  const usable = useMemo(
    () => tiles.filter((t) => !failed[String(t.key)]).slice(0, 3),
    [tiles, failed]
  );

  const [hero, ...rest] = usable;
  const supporting = rest.slice(0, 2);
  const remainder = total - usable.length;

  return (
    <div className="relative shrink-0 h-[148px] bg-surface-2">
      {tiles.length === 0 ? (
        <div className="absolute inset-0 m-3 flex flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-border-token">
          <span className="text-text-2" style={{ fontSize: 12.5 }}>
            {emptyTitle}
          </span>
          {emptyAction && (
            <button
              type="button"
              className="text-accent-text font-bold"
              style={{ fontSize: 12.5 }}
              onClick={(e) => {
                e.stopPropagation();
                emptyAction.onClick();
              }}
            >
              {emptyAction.label}
            </button>
          )}
        </div>
      ) : (
        <div className="absolute inset-0 flex gap-px">
          <Frame
            tile={hero}
            className="flex-1 h-full"
            onFail={hero ? () => markFailed(hero.key) : undefined}
          />
          {supporting.length > 0 && (
            <div className="w-[34%] shrink-0 flex flex-col gap-px">
              {supporting.map((t) => (
                <Frame
                  key={t.key}
                  tile={t}
                  className="flex-1 w-full"
                  onFail={() => markFailed(t.key)}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {tiles.length > 0 && (
        <span className="absolute bottom-2 left-2">
          <TechChip>{countLabel}</TechChip>
        </span>
      )}

      {remainder > 0 && usable.length > 0 && (
        <span className="absolute bottom-2 right-2">
          <TechChip>+{remainder}</TechChip>
        </span>
      )}
    </div>
  );
};

export default ShortlistMosaic;
